import React, { useEffect, useState } from 'react';
import { 
  AppBar, Toolbar, Grid, Card, Typography, Box, TextField, 
  Button, Alert, CircularProgress 
} from '@mui/material';
import logo from '../images/logo.ico';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials } from '../features/authSlice';
import { useLoginUserMutation } from '../services/userAuthApi';

const UserLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [server_error, setServerError] = useState({});
  const [loginError, setLoginError] = useState('');
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { access_token } = useSelector(state => state.auth);
  
  const [loginUser, { isLoading }] = useLoginUserMutation();

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userInfo = localStorage.getItem('userInfo');
    
    if (token && userInfo) {
      try {
        const user = JSON.parse(userInfo);
        dispatch(setCredentials({
          access_token: token,
          user: user
        }));
        navigate('/admin/dashboard', { replace: true });
      } catch (error) {
        console.error('Error parsing user info:', error);
        // Clear invalid data
        localStorage.removeItem('access_token');
        localStorage.removeItem('userInfo');
      }
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user types
    setServerError({});
    setLoginError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setServerError({});

    // Client-side validation
    if (!formData.email || !formData.password) {
      setLoginError('Please fill in all fields');
      return;
    }

    try {
      const res = await loginUser(formData).unwrap();
      
      if (res && res.token) {
        // Store token in localStorage
        if (res.token.access) {
          localStorage.setItem('access_token', res.token.access);
        }
        if (res.token.refresh) {
          localStorage.setItem('refresh_token', res.token.refresh);
        }

        // Create user object
        const user = {
          id: res.token.id,
          first_Name: res.token.first_Name,
          last_Name: res.token.last_Name,
          type: res.token.type,
          allowed_pages: res.token.allowed_pages || [],
          email: formData.email
        };
        
        // Store user info
        localStorage.setItem('userInfo', JSON.stringify(user));

        // Dispatch to Redux
        dispatch(setCredentials({
          access_token: res.token.access,
          refresh_token: res.token.refresh,
          user: user
        }));

        // Navigate to dashboard
        navigate('/admin/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      
      if (error?.data?.errors) {
        setServerError(error.data.errors);
      } else if (error?.data?.detail) {
        setLoginError(error.data.detail);
      } else if (error?.data?.non_field_errors) {
        setServerError({ non_field_errors: error.data.non_field_errors });
      } else {
        setLoginError('An unexpected error occurred. Please try again.');
      }
    }
  };

  // If already logged in (from Redux state), redirect
  useEffect(() => {
    if (access_token) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [access_token, navigate]);

  return (
    <div>
      <Box sx={{ flexGrow: 1, width: '100%', overflowX: 'hidden' }}>
        <AppBar position="static" sx={{ backgroundColor: '#f8f9fa', width: '100%' }}>
          <Toolbar sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            padding: { xs: '10px 0', sm: '0 16px' }
          }}>
            <Box 
              component="img" 
              sx={{ height: { xs: 70, sm: 100 }, margin: { xs: '0 0 10px 0', sm: '0 20px 0 0' } }} 
              alt="logo" 
              src={logo} 
            />
            <Typography 
              variant='h4' 
              component="div" 
              sx={{ 
                flexGrow: 1, 
                color: '#004aad', 
                fontWeight: 'bold', 
                textAlign: 'center', 
                fontSize: { xs: '1.8rem', sm: '2.125rem' } 
              }}
            >
              ADMIN PANEL
            </Typography>
          </Toolbar>
        </AppBar>
      </Box>

      <Grid 
        container 
        sx={{ 
          display: "flex", 
          justifyContent: "center", 
          margin: { xs: '15px 0', sm: '30px' }, 
          padding: { xs: '0 10px', sm: '0' } 
        }}
      >
        <Grid item xs={12} sm={8} md={6} lg={5}>
          <Card 
            sx={{ 
              width: "100%", 
              marginTop: { xs: '15px', sm: '30px' }, 
              padding: { xs: '10px', sm: '20px' } 
            }}
          >
            <Box sx={{ mx: { xs: 2, sm: 3 } }}>
              <h3 style={{ 
                color: "#004aad", 
                fontSize: 33, 
                textAlign: "center", 
                marginTop: '30px', 
                fontWeight: 'bold', 
                marginBottom: '30px' 
              }}>
                Admin Login
              </h3>

              <Box 
                component="form" 
                noValidate 
                sx={{ mt: 1 }} 
                id="login-form" 
                onSubmit={handleSubmit}
              >
                <TextField 
                  margin="normal" 
                  required 
                  fullWidth 
                  id="email" 
                  name="email" 
                  label="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  autoFocus
                />
                {server_error.email && (
                  <Typography style={{ fontSize: 14, color: "red", paddingLeft: 10, marginTop: 5 }}>
                    {server_error.email[0]}
                  </Typography>
                )}

                <TextField 
                  margin="normal" 
                  required 
                  fullWidth 
                  id="password" 
                  name="password" 
                  label="Password" 
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  autoComplete="current-password"
                />
                {server_error.password && (
                  <Typography style={{ fontSize: 14, color: "red", paddingLeft: 10, marginTop: 5 }}>
                    {server_error.password[0]}
                  </Typography>
                )}

                <Box textAlign="center" sx={{ mt: 3, mb: 2 }}>
                  {isLoading ? (
                    <CircularProgress />
                  ) : (
                    <Button 
                      type="submit" 
                      variant="contained" 
                      sx={{ 
                        mt: 3, 
                        mb: 2, 
                        px: 5, 
                        backgroundColor: '#004aad',
                        fontSize: '1rem'
                      }}
                    >
                      Login
                    </Button>
                  )}
                </Box>

                {server_error.non_field_errors && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {server_error.non_field_errors[0]}
                  </Alert>
                )}
                
                {loginError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {loginError}
                  </Alert>
                )}
              </Box>
            </Box>

            <Box textAlign="center" sx={{ mt: 2, mb: 2 }}>
              <Box component="img" sx={{ height: 150 }} alt="logo" src={logo} />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default UserLogin;